import React from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ALL_DETAIL_ROUTES_INFO } from "../../constant/HeaderData";
import { COMPANY_DATA } from "../../constant/CompanyData";
import BrandPartnersMain from "../company/BrandPartners/BrandPartnersMain";
import Testimonials from "../company/Testimonials";
import Careers from "../company/Careers";
import AboutUs from "../company/AboutUs";
import CertificatesSection from "../company/CertificatesSection";
import ServicesItems from "../Services/ServicesItems";
import Badge from "../common/Badge";

const DetailSection = () => {
  const { pageId } = useParams();
  const location = useLocation();
  const currentRoute = location.pathname;

  const currentPageInfo = ALL_DETAIL_ROUTES_INFO.find(
    (item) => item.pageId === pageId
  );

  const detailImageURL = currentPageInfo?.detailImageURL
    ? require(`../../assets/HeroSectionImages/DetailImages/${currentPageInfo.detailImageURL}`)
    : require("../../assets/HeroSectionImages/it_consultation.webp");

  const renderComponent = () => {
    if (currentRoute.includes("services")) {
      return <ServicesItems />;
    }

    if (currentPageInfo) {
      const pageId = currentPageInfo.pageId;

      switch (pageId) {
        case "brand-partners":
          return <BrandPartnersMain />;
        case "testimonials":
          return <Testimonials />;
        case "about-us":
          return <AboutUs />;
        case "careers":
          return <Careers />;
        case "certificates":
          return <CertificatesSection />;
        default:
          return null;
      }
    }
  };

  const pageTitle = currentPageInfo
    ? `${currentPageInfo.title} - QuantaRock`
    : "QuantaRock Systems";
  const pageDescription =
    currentPageInfo?.description ||
    "QuantaRock delivers secure colocation, cloud hosting, dedicated servers, and IT infrastructure engineering.";
  const canonicalUrl = `https://quantarock.in${currentRoute}`;

  const isServicesRoute = currentRoute.includes("services");
  const isCareersRoute = pageId === "careers";

  const heroCta = isServicesRoute
    ? { label: "Request a quote", to: "/contact" }
    : isCareersRoute
      ? { label: "View open positions", href: COMPANY_DATA.careerFormLink }
      : { label: "Get in touch", to: "/contact" };

  return (
    <div>
      {!isServicesRoute && (
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
          <meta name="robots" content="index,follow" />
          <link rel="canonical" href={canonicalUrl} />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={pageDescription} />
          <meta property="og:url" content={canonicalUrl} />
        </Helmet>
      )}
      <section className="relative flex min-h-[400px] items-center overflow-hidden bg-[var(--surface-dark)] py-20 md:min-h-[520px]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${detailImageURL})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[rgba(var(--surface-dark-rgb),0.95)] via-[rgba(var(--surface-dark-rgb),0.82)] to-[rgba(var(--surface-dark-rgb),0.45)]" />
        <div className="relative z-10 mx-auto w-full max-w-4xl px-6 text-center">
          <Badge>
            {currentRoute.includes("services") ? "Services" : "QuantaRock Systems"}
          </Badge>
          <h1
            style={{
              textShadow:
                "0 0 1px rgba(0,0,0,1), 0 0 3px rgba(0,0,0,1), 0 1px 4px rgba(0,0,0,0.95), 0 2px 10px rgba(0,0,0,0.9), 0 6px 20px rgba(0,0,0,0.7)",
            }}
            className="mx-auto max-w-4xl text-4xl font-bold leading-[1.08] text-white sm:text-5xl md:text-6xl"
          >
            {currentPageInfo ? currentPageInfo.title : "Default Title"}
          </h1>
          <p
            style={{
              textShadow:
                "0 0 1px rgba(0,0,0,1), 0 0 2px rgba(0,0,0,1), 0 1px 3px rgba(0,0,0,0.9), 0 4px 12px rgba(0,0,0,0.75)",
            }}
            className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-200 sm:text-lg"
          >
            {currentPageInfo
              ? currentPageInfo.description
              : "Default description."}
          </p>
          {heroCta.to ? (
            <Link
              to={heroCta.to}
              className="mt-8 inline-flex items-center justify-center rounded-full bg-[var(--website-secondary-color)] px-8 py-4 text-sm font-semibold text-[var(--on-secondary)] shadow-lg shadow-[rgba(var(--website-secondary-rgb),0.2)] transition hover:scale-[1.02] hover:brightness-110"
            >
              {heroCta.label}
            </Link>
          ) : (
            <a
              href={heroCta.href}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center justify-center rounded-full bg-[var(--website-secondary-color)] px-8 py-4 text-sm font-semibold text-[var(--on-secondary)] shadow-lg shadow-[rgba(var(--website-secondary-rgb),0.2)] transition hover:scale-[1.02] hover:brightness-110"
            >
              {heroCta.label}
            </a>
          )}
        </div>
      </section>

      {renderComponent()}
    </div>
  );
};

export default DetailSection;
